import {
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
  useMemo
} from 'react';
import { io } from 'socket.io-client';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext, SnackContext } from 'src/store/ContextStore';

const HEARTBEAT_INTERVAL_MS = 30_000; // eslint-disable-line no-unused-vars

// TGCLOUD-1767 — `personId` is a String on the wire and in the database (the
// column is `type: String`), but the canvas hands it over as a Number: `pid` is
// declared `type: Number` on the member. Every socket payload therefore keys on
// "5" while every local call site keys on 5, and a Map does not consider those
// the same key.
//
// The visible symptom was a lock that outlived its owner: a mid-session
// `person:lock-force-acquired` wrote "5", so `isMine(5)` kept reading the stale
// optimistic entry and stayed true after you had been displaced.
//
// So every read and write of the two Maps below goes through this, and the hook
// exposes `getLock` / `getRequestStatus` so no caller outside this file has to
// remember to normalise.
const lockKey = (personId) =>
  personId === null || personId === undefined ? null : String(personId);

/**
 * Manages person-level locks for a pedigree.
 *
 * Every route is family-scoped: a lock is identified by (pedigreeId, personId),
 * because `personId` alone is the canvas-local pid and the proband of every
 * family is pid 1. See backend/routes/person-lock.js.
 *
 * Returns a hook object with:
 *   lockedPersons        Map<String personId, lockInfo>  — all active locks for this pedigree
 *   getLock(pid)         lockInfo | undefined — Number- or String-keyed lookup
 *   isLockedByOther(pid) boolean — true if the person is locked by someone else
 *   isMine(pid)          boolean — true if the current user holds the lock
 *   getRequestStatus(pid) 'pending'|'granted'|'denied'|undefined
 *   acquireLock(pid)     async → { success, conflict? }
 *   releaseLock(pid)     async → void
 *   releaseBeacon(pid)   → void  (unload-safe release, no promise to await)
 *   requestLock(pid)     → void  (sends access request to holder)
 *   forceAcquireLock(pid) async → void
 *   acquireBatch(pids)   async → { success, conflicts? }
 *   releaseBatch(pids)   async → void
 */
// TGCLOUD-1669 Phase 7 - one lock per (family, person), however many mounts
// hold it.
//
// THE BUG THIS EXISTS FOR. A lock row is identified by (pedigreeId, personId)
// and carries a sessionId that no route ever filters on, so the server cannot
// tell two mounts by the same user apart. Acquiring twice is harmless (the
// second upserts the same row), but `release` deletes by
// (pedigreeId, personId, lockedByUserId) with no session term, so whichever
// mount closes FIRST destroys the lock the other still believes it holds. The
// survivor is usually a hidden tab, whose socket is torn down and whose
// optimistic map is never cleared, so it goes on reporting the lock as its own
// against a row that no longer exists, with its sections still editable.
//
// That is the same class of defect as TGCLOUD-1767, a lock outliving its owner,
// at a new seam: it appears the moment a person tab and a pedigree tab are open
// on the same person.
//
// The counter is keyed on the same pair the server uses, so client and server
// agree on identity. Module level rather than per hook because the two mounts
// are two component trees; a ref would count each of them separately, which is
// the bug.
const lockHolders = new Map();

const holderKey = (familyId, personId) => `${familyId}:${String(personId)}`;

const addHolder = (familyId, personId) => {
  const key = holderKey(familyId, personId);
  const next = (lockHolders.get(key) || 0) + 1;
  lockHolders.set(key, next);
  // Only the FIRST holder posts. The rest are passive readers of a lock that
  // is already held by this user.
  return next === 1;
};

const dropHolder = (familyId, personId) => {
  const key = holderKey(familyId, personId);
  const next = (lockHolders.get(key) || 1) - 1;
  if (next <= 0) {
    lockHolders.delete(key);
    return true;
  }
  lockHolders.set(key, next);
  return false;
};

// Test seam. The counter is module state, so a suite that acquires without
// releasing would leak into the next test.
export const __resetLockHolders = () => lockHolders.clear();

export function usePersonLocks(familyId, options = {}) {
  // options.active (default true) — only the visible/active tab keeps a live
  // person-lock socket. This socket only OBSERVES (its teardown releases nothing),
  // so a background tab can safely drop it and re-hydrate on activation.
  const { active = true } = options;
  const { user } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);

  // Map<String personId, lockInfo>
  const [lockedPersons, setLockedPersons] = useState(new Map());
  // Map<String personId, 'pending'|'granted'|'denied'>
  const [requestStatuses, setRequestStatuses] = useState(new Map());
  // Map<String personId, String> — the holder's optional reason for declining,
  // kept beside the status rather than folded into it so `getRequestStatus`
  // stays a plain string for every existing caller (TGCLOUD-1770).
  const [declineMessages, setDeclineMessages] = useState(new Map());
  // Persistent banner state when someone requests access to a person we hold
  // null | { requesterName, requesterId, personId }
  const [pendingPersonRequest, setPendingPersonRequest] = useState(null);

  const socketRef = useRef(null);
  const sessionId = useRef(String(Date.now() + Math.random()));

  const userId = user?._id ? String(user._id) : null;

  // Base for every person-lock URL. `familyId` is a path segment now rather
  // than a body field: a lookup that loses it does not narrow, it goes global.
  //
  // Returns null rather than a `/person-lock/null/...` URL when there is nothing
  // to scope to. PedigreeChartView passes `familyId: null` in PREVIEW mode while
  // still handing the hook to PedigreeSideBar, whose select effect calls
  // acquireLock unconditionally — which used to take a real, GLOBAL lock on that
  // pid. Every mutating call below refuses on a null URL.
  const lockUrl = useCallback(
    (personId, suffix = '') => {
      const key = lockKey(personId);
      if (!familyId || key === null) return null;
      return `${APICONSTANTS.personLock}/${encodeURIComponent(
        familyId
      )}/${encodeURIComponent(key)}${suffix}`;
    },
    [familyId]
  );

  const getLock = useCallback(
    (personId) => lockedPersons.get(lockKey(personId)),
    [lockedPersons]
  );

  const getRequestStatus = useCallback(
    (personId) => requestStatuses.get(lockKey(personId)),
    [requestStatuses]
  );

  const getDeclineMessage = useCallback(
    (personId) => declineMessages.get(lockKey(personId)),
    [declineMessages]
  );

  const isLockedByOther = useCallback(
    (personId) => {
      if (!personId) return false;
      const lock = lockedPersons.get(lockKey(personId));
      if (!lock) return false;
      return String(lock.lockedByUserId) !== userId;
    },
    [lockedPersons, userId]
  );

  const isMine = useCallback(
    (personId) => {
      if (!personId) return false;
      const lock = lockedPersons.get(lockKey(personId));
      if (!lock) return false;
      return String(lock.lockedByUserId) === userId;
    },
    [lockedPersons, userId]
  );

  // Set up socket connection and listen for lock events
  useEffect(() => {
    if (!familyId || !active) return;

    const socket = io('', { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('pedigree:join', { familyId });
    });

    // Hydrate all current locks on join
    socket.on('person:locks-batch', ({ locks }) => {
      setLockedPersons((prev) => {
        const next = new Map(prev);
        locks.forEach(({ personId, lock }) => {
          next.set(lockKey(personId), lock);
        });
        return next;
      });
    });

    // A single person lock was acquired
    socket.on('person:lock-acquired', ({ personId, lock }) => {
      setLockedPersons((prev) => new Map(prev).set(lockKey(personId), lock));
    });

    // A single person lock was released
    socket.on('person:lock-released', ({ personId }) => {
      const key = lockKey(personId);
      setLockedPersons((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      setPendingPersonRequest((prev) =>
        lockKey(prev?.personId) === key ? null : prev
      );
      // Clear pending request status so the requester's UI updates
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    });

    // Batch release
    socket.on('person:locks-released', ({ personIds }) => {
      setLockedPersons((prev) => {
        const next = new Map(prev);
        personIds.forEach((pid) => next.delete(lockKey(pid)));
        return next;
      });
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        personIds.forEach((pid) => next.delete(lockKey(pid)));
        return next;
      });
    });

    // Force acquire
    socket.on('person:lock-force-acquired', ({ personId, lock }) => {
      const key = lockKey(personId);
      setLockedPersons((prev) => new Map(prev).set(key, lock));
      setPendingPersonRequest((prev) =>
        lockKey(prev?.personId) === key ? null : prev
      );
      // Someone else took the lock — clear any pending request we had for it
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    });

    // Someone is requesting access to a person we hold — show persistent banner
    socket.on(
      'person:lock-request-received',
      ({ requesterName, requesterId, personId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingPersonRequest({
          requesterName,
          requesterId,
          personId: lockKey(personId)
        });
      }
    );

    // Requester cancelled their person lock request — clear our banner
    socket.on(
      'person:lock-request-cancelled',
      ({ personId, requesterId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingPersonRequest((prev) =>
          lockKey(prev?.personId) === lockKey(personId) &&
          String(prev.requesterId) === String(requesterId)
            ? null
            : prev
        );
      }
    );

    // Response to our own access request.
    //
    // TGCLOUD-1770: until the holder was given Release / Decline actions this
    // event could never fire, because nothing on the client emitted
    // `person:lock-release-response` — so this handler, the 'granted'/'denied'
    // statuses and SidebarLockBanner's "Your request was declined" branch were
    // all unreachable.
    socket.on(
      'person:lock-request-result',
      ({ personId, granted, declineMessage, targetUserId }) => {
        // The response is broadcast to the room, so ignore one addressed to
        // another requester (mirrors the request-received handler above).
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        const key = lockKey(personId);
        setRequestStatuses((prev) => {
          const next = new Map(prev);
          next.set(key, granted ? 'granted' : 'denied');
          return next;
        });
        setDeclineMessages((prev) => {
          const next = new Map(prev);
          if (!granted && declineMessage) next.set(key, declineMessage);
          else next.delete(key);
          return next;
        });
        if (!granted) {
          setSnack({
            status: true,
            severity: 'warning',
            msg: declineMessage
              ? `Your request for edit access was declined: ${declineMessage}`
              : 'Your request for edit access was declined.'
          });
        }
      }
    );

    return () => {
      socket.emit('pedigree:leave', { familyId });
      socket.disconnect();
      socketRef.current = null;
      // Drop the optimistic view with the socket that fed it (TGCLOUD-1669
      // Phase 7). Keeping it meant a tab that went inactive froze whatever it
      // last believed: a lock still shown as mine after another mount released
      // it, a "Requesting..." that never resolves, a holder banner naming
      // someone who has long since let go. There is nothing left to correct it,
      // because the events that would have arrive on the socket just closed.
      //
      // Nothing is lost by clearing: rejoining replays person:locks-batch,
      // which is the source of truth and already the path used on first join.
      // Only when there is something to drop. An unconditional clear looks
      // harmless and is not: `lockedPersons` is a dependency of ChartBox's
      // sidebar memo, so replacing an already-empty Map with a different empty
      // Map changed its identity on EVERY activation flip and re-rendered the
      // whole sidebar in both tabs of every switch. Bailing out preserves the
      // fix (stale optimistic state is dropped with the socket that fed it)
      // and costs nothing when there was nothing to drop.
      setLockedPersons((prev) => (prev.size ? new Map() : prev));
      setRequestStatuses((prev) => (prev.size ? new Map() : prev));
      setPendingPersonRequest((prev) => (prev == null ? prev : null));
    };
  }, [familyId, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const acquireLock = useCallback(
    async (personId) => {
      const key = lockKey(personId);
      const url = lockUrl(personId, '/acquire');
      if (!url) return { success: false };
      // A second mount on the same person joins the lock the first one holds
      // rather than taking its own. Reported as success, because it IS held.
      if (!addHolder(familyId, personId))
        return { success: true, shared: true };
      try {
        const res = await axios.post(url, {
          sessionId: socketRef.current?.id || sessionId.current
        });
        if (res.data.acquired) {
          setLockedPersons((prev) => new Map(prev).set(key, res.data.lock));
          return { success: true };
        }
        // Acquire failed — populate lockedPersons immediately so lockedByOther
        // is true without waiting for the socket event
        if (res.data.lock) {
          setLockedPersons((prev) => new Map(prev).set(key, res.data.lock));
        }
        // The lock was not taken, so this mount is not a holder. Leaving the
        // count raised would let a later release be swallowed.
        dropHolder(familyId, personId);
        return { success: false, conflict: res.data.lock };
      } catch (err) {
        dropHolder(familyId, personId);
        if (err.response?.status === 409) {
          const conflictLock = err.response.data.lock;
          if (conflictLock) {
            setLockedPersons((prev) => new Map(prev).set(key, conflictLock));
          }
          return { success: false, conflict: conflictLock };
        }
        console.error('Failed to acquire person lock', err);
        return { success: false };
      }
    },
    [lockUrl, familyId]
  );

  const releaseLock = useCallback(
    async (personId) => {
      const url = lockUrl(personId, '/release');
      if (!url) return;
      // Another mount still has this person open. Releasing now would delete
      // the row out from under it.
      if (!dropHolder(familyId, personId)) return;
      try {
        await axios.post(url);
        setLockedPersons((prev) => {
          const next = new Map(prev);
          next.delete(lockKey(personId));
          return next;
        });
      } catch (err) {
        console.error('Failed to release person lock', err);
      }
    },
    [lockUrl, familyId]
  );

  // Unload-safe release. `beforeunload` cannot await a promise, so this is the
  // one call that bypasses the axios instance — which is exactly why it used to
  // hardcode '/api/...' and would 404 wherever PUBLIC_URL is non-empty
  // (TGCLOUD-1767). Reading the base off the instance means the two cannot drift.
  const releaseBeacon = useCallback(
    (personId) => {
      const url = lockUrl(personId, '/release');
      if (!url || !navigator?.sendBeacon) return;
      const base = axios.defaults.baseURL || '';
      navigator.sendBeacon(`${base}${url}`);
    },
    [lockUrl]
  );

  const sendHeartbeat = useCallback(
    async (personId) => {
      const url = lockUrl(personId, '/heartbeat');
      if (!url) return;
      try {
        await axios.post(url);
      } catch (_) {
        // ignore
      }
    },
    [lockUrl]
  );

  const requestLock = useCallback(
    (personId) => {
      const key = lockKey(personId);
      const lock = lockedPersons.get(key);
      const url = lockUrl(personId, '/request');
      if (!lock || !url) return;
      setRequestStatuses((prev) => new Map(prev).set(key, 'pending'));
      axios
        .post(url, {
          lockHolderUserId: lock.lockedByUserId
        })
        .catch(() =>
          setRequestStatuses((prev) => {
            const next = new Map(prev);
            next.delete(key);
            return next;
          })
        );
    },
    [lockUrl, lockedPersons]
  );

  const cancelRequestLock = useCallback(
    (personId) => {
      const key = lockKey(personId);
      const lock = lockedPersons.get(key);
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      const socket = socketRef.current;
      if (socket && lock) {
        socket.emit('person:lock-request-cancel', {
          pedigreeId: familyId,
          personId: key,
          lockHolderUserId: lock.lockedByUserId
        });
      }
    },
    [familyId, lockedPersons]
  );

  // TGCLOUD-1770 — the lock holder answers an incoming access request.
  //
  // Mirrors usePedigreeLock.respondToRequest, which is the version that already
  // worked: emit the result, and on "granted" also RELEASE the lock, because
  // saying yes without letting go leaves the requester unable to acquire and
  // looks identical to being ignored.
  //
  // Before this, the holder's banner offered only "dismiss" and the text asked
  // them to deselect the person manually, so `person:lock-release-response` was
  // emitted by nothing and the requester sat on "Requesting..." until the
  // 10-minute force-take timer expired.
  const respondToPersonRequest = useCallback(
    (granted, declineMessage) => {
      const req = pendingPersonRequest;
      if (!req) return;
      setPendingPersonRequest(null);
      const socket = socketRef.current;
      if (socket) {
        socket.emit('person:lock-release-response', {
          pedigreeId: familyId,
          personId: lockKey(req.personId),
          granted,
          requesterId: req.requesterId,
          declineMessage: declineMessage || null
        });
      }
      if (granted) releaseLock(req.personId);
    },
    [familyId, pendingPersonRequest, releaseLock]
  );

  const forceAcquireLock = useCallback(
    async (personId) => {
      const url = lockUrl(personId, '/force-acquire');
      if (!url) return;
      try {
        const res = await axios.post(url, {
          sessionId: socketRef.current?.id || sessionId.current
        });
        if (res.data.acquired) {
          setLockedPersons((prev) =>
            new Map(prev).set(lockKey(personId), res.data.lock)
          );
        }
      } catch (err) {
        console.error('Failed to force-acquire person lock', err);
      }
    },
    [lockUrl]
  );

  const acquireBatch = useCallback(
    async (personIds) => {
      if (!familyId) return { success: false, conflicts: [] };
      try {
        const res = await axios.post(
          `${APICONSTANTS.personLock}/${encodeURIComponent(
            familyId
          )}/batch/acquire`,
          {
            personIds,
            sessionId: socketRef.current?.id || sessionId.current
          }
        );
        if (res.data.acquired) {
          setLockedPersons((prev) => {
            const next = new Map(prev);
            res.data.locks.forEach((l) => next.set(lockKey(l.personId), l));
            return next;
          });
          return { success: true };
        }
        return { success: false, conflicts: res.data.conflicts };
      } catch (err) {
        if (err.response?.status === 409) {
          return {
            success: false,
            conflicts: err.response.data.conflicts
          };
        }
        console.error('Failed to acquire batch person locks', err);
        return { success: false, conflicts: [] };
      }
    },
    [familyId]
  );

  const releaseBatch = useCallback(
    async (personIds) => {
      if (!familyId) return;
      try {
        await axios.post(
          `${APICONSTANTS.personLock}/${encodeURIComponent(
            familyId
          )}/batch/release`,
          { personIds }
        );
        setLockedPersons((prev) => {
          const next = new Map(prev);
          personIds.forEach((pid) => next.delete(lockKey(pid)));
          return next;
        });
      } catch (err) {
        console.error('Failed to release batch person locks', err);
      }
    },
    [familyId]
  );

  // MEMOISED, and it is not cosmetic (TGCLOUD-1775).
  //
  // A bare object literal here gets a new identity on every render, and this
  // object is threaded down PedigreeChartView -> PedigreeBox -> PedigreeChart
  // -> ChartBox, where several memos depend on it. So it invalidated all of
  // them on every render, which is why a measured tab switch committed EVERY
  // open tab rather than the two whose active flag moved. Every member below is
  // already a useState value or a useCallback, so this only stops the wrapper
  // churning.
  return useMemo(
    () => ({
      lockedPersons,
      getLock,
      isLockedByOther,
      isMine,
      requestStatuses,
      getRequestStatus,
      pendingPersonRequest,
      respondToPersonRequest,
      getDeclineMessage,
      acquireLock,
      releaseLock,
      releaseBeacon,
      sendHeartbeat,
      requestLock,
      cancelRequestLock,
      forceAcquireLock,
      acquireBatch,
      releaseBatch
    }),
    [
      lockedPersons,
      getLock,
      isLockedByOther,
      isMine,
      requestStatuses,
      getRequestStatus,
      pendingPersonRequest,
      respondToPersonRequest,
      getDeclineMessage,
      acquireLock,
      releaseLock,
      releaseBeacon,
      sendHeartbeat,
      requestLock,
      cancelRequestLock,
      forceAcquireLock,
      acquireBatch,
      releaseBatch
    ]
  );
}
